// COACHING PERSONALITY — API client (WP2 surface, WP1/WP3 endpoints).
//
// Isolated from the shared services/api.js exactly like lastMinuteService.js and
// clubNetworkApi.js: a small PR surface and no risk to the shared axios
// instance. Every path below was read off the backend worktree
// (users/coaching_personality_views.py + users/urls.py:360-378), never guessed:
//
//   GET    /users/coaching-personality/                 the owner's whole state
//   POST   /users/coaching-personality/start/           begin a run OR retake
//   GET    /users/coaching-personality/questionnaire/   resume where you stopped
//   PATCH  /users/coaching-personality/draft/           save answers (MERGES)
//   DELETE /users/coaching-personality/draft/           discard the run
//   POST   /users/coaching-personality/complete/        draft becomes a result
//   POST   /users/coaching-personality/release/         the ONE toggle
//   DELETE /users/coaching-personality/result/          delete, lock survives
//   GET    /users/coaching-personality/history/         the owner's own history
//   GET    /users/coaching-personality/card/<id>/       the club side card
//   GET    /users/coaching-personality/card/coach/<ts>/ the same card, by TheSports id
//
// THE CLIENT NEVER COMPUTES A RESULT. Sums, level, short label and marker
// position all arrive worked out (coaching_personality.py:165 axis_readout).
// Nothing in this file, and nothing that consumes it, does arithmetic on an
// answer.
//
// 404 IS "THERE IS NOTHING", NOT AN ERROR. The card endpoint answers 404
// identically for all five reasons a card is not served (see the view's module
// docstring), and the owner endpoints answer 404 when the section does not
// apply to this person at all. So every reader below distinguishes `notFound`
// from `success`, and the surface renders nothing rather than an error box.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — the request proceeds and the server answers 401 */
  }
  return config;
});

const BASE = "/users/coaching-personality";

// A refusal the surface has to tell apart from a failure:
//   notFound  → there is nothing here (render nothing, never an error)
//   code      → 'retake_locked' (409) or a validation code (400)
const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  notFound: error?.response?.status === 404,
  code: error?.response?.data?.code || null,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

export const coachingPersonalityService = {
  // The one call the profile section opens with. Answers state (empty | draft |
  // retaking | completed), the draft block, the result, the lock and the
  // evaluation intro in a single round trip.
  getState: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Start and retake are one endpoint because they are one act with one gate
  // (the six month lock). Returns the questionnaire payload.
  start: async () => {
    try {
      const res = await authApi.post(`${BASE}/start/`, {});
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getQuestionnaire: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/questionnaire/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // PATCH merges server side, so this may send one answer or forty.
  saveAnswers: async (answers) => {
    try {
      const res = await authApi.patch(`${BASE}/draft/`, { answers });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  discardDraft: async () => {
    try {
      await authApi.delete(`${BASE}/draft/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // The trailing answers may travel with the completion so question 50 and
  // "Finish" are one round trip; the server merges them onto the draft.
  complete: async (answers) => {
    try {
      const res = await authApi.post(`${BASE}/complete/`, { answers });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  setReleased: async (isReleased) => {
    try {
      const res = await authApi.post(`${BASE}/release/`, {
        is_released: isReleased,
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  deleteResult: async () => {
    try {
      const res = await authApi.delete(`${BASE}/result/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // The private development comparison. Owner only, oldest first, no export.
  getHistory: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/history/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // The club side card, keyed on the ACCOUNT id. A 404 here means "render
  // nothing": no toggle, no pitch, no result, no profile, or no such account —
  // the five are indistinguishable on purpose.
  getCard: async (accountId, signal) => {
    try {
      const res = await authApi.get(`${BASE}/card/${accountId}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // The SAME card, named the other way. A club on a coach profile holds a
  // TheSports id and no account id, because the unauthenticated public coach
  // payload withholds the account id on purpose next to email, phone number and
  // address (users/serializers.py PublicCoachSerializer, "Remove sensitive
  // fields"). Publishing it there to make this authenticated endpoint reachable
  // would have paid for this feature out of another endpoint's privacy, so the
  // backend added a second route on the id the coach page already has
  // (coaching_personality_views.py:519, urls.py:383). Same visibility check,
  // same 404: both routes end in the same _card_response.
  getCardByCoachId: async (tsCoachId, signal) => {
    try {
      const res = await authApi.get(`${BASE}/card/coach/${tsCoachId}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },
};

export default coachingPersonalityService;
